import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Save, Zap, Loader2, Clock, Repeat, Bell, Power, Gauge, Hand,
  MessageSquare, ListChecks, Eye, RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cadenceAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Lead Communication Governance — status/activity/frequency aware SMS
 * nurture controls. Backed by `system_settings.auto_reminder_cadence`;
 * every change takes effect on the next hourly scheduler tick.
 */

const STATE_META = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  paused_by_contact: { label: 'Paused — Reply', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  paused_by_appointment: { label: 'Paused — Appointment', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  paused_by_agent: { label: 'Paused — Agent', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  paused_by_status: { label: 'Paused — Status', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  paused_by_frequency: { label: 'Paused — Frequency', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  do_not_contact: { label: 'Do Not Contact', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  not_eligible: { label: 'Not Eligible', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const STATUS_ROWS = [
  { key: 'new', label: 'New', hint: 'Not yet contacted' },
  { key: 'attempting_contact', label: 'Attempting Contact', hint: 'Tried, no reach' },
  { key: 'contacted', label: 'Contacted', hint: 'Spoke with lead' },
  { key: 'quoted', label: 'Quoted', hint: 'Quote sent — generic nurture off' },
  { key: 'appointment', label: 'Appointment', hint: 'Reminders only, no sales nurture' },
  { key: 'closed_won', label: 'Closed Won', hint: 'Nurture stops' },
  { key: 'not_interested', label: 'Not Interested', hint: 'Nurture stops' },
];

export default function AutoReminderSettingsPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await cadenceAPI.get();
      setCfg(data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  })(); }, []);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await cadenceAPI.preview();
      setPreview(data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load examples'));
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const set = (k, v) => { setCfg((c) => ({ ...c, [k]: v })); setDirty(true); };
  const setStatus = (key, allow) => {
    setCfg((c) => ({
      ...c,
      status_policy: { ...(c.status_policy || {}), [key]: { allow } },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await cadenceAPI.update(cfg);
      setCfg(data);
      setDirty(false);
      toast.success('Saved — takes effect on the next hourly tick');
      loadPreview();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  if (loading || !cfg) return <p className="p-10 text-center text-sm text-slate-500">Loading…</p>;

  const sp = cfg.status_policy || {};

  return (
    <div data-testid="lead-communication-governance-page">
      <PageHeader
        title="Lead Communication Governance"
        description="Status-aware, activity-aware, cost-conscious control of automated lead nurture."
        actions={
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="save-cadence-btn">
            {saving ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</> : <><Save size={14} className="mr-1.5" /> Save changes</>}
          </Button>
        }
      />
      <div className="page-content max-w-4xl space-y-4">
        {/* Master switch */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  <Power size={18} />
                </div>
                <div>
                  <div className="font-semibold text-sm">Automated lead nurture</div>
                  <div className="text-xs text-slate-500">
                    {cfg.enabled
                      ? 'Active — eligible HOT/WARM leads receive governed automated touches.'
                      : 'Paused. No automated nurture is sent.'}
                  </div>
                </div>
              </div>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="cadence-enabled-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Frequency & cost controls */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
              <Gauge size={14} /> Frequency &amp; cost safeguards
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Max automated SMS / week" hint="Hard cap per lead">
                <Input type="number" min={0} max={20} value={cfg.max_sms_per_week}
                  onChange={(e) => set('max_sms_per_week', parseInt(e.target.value, 10) || 0)}
                  data-testid="cadence-max-week" />
              </Field>
              <Field label="Cooldown between touches (days)" hint="Suggested 5–7 days">
                <Input type="number" min={0} max={60} value={cfg.generic_cooldown_days}
                  onChange={(e) => set('generic_cooldown_days', parseInt(e.target.value, 10) || 0)}
                  data-testid="cadence-cooldown-days" />
              </Field>
              <Field label="Maximum attempts (ever)" hint="Stop after this many touches">
                <Input type="number" min={1} max={10} value={cfg.max_nudges}
                  onChange={(e) => set('max_nudges', parseInt(e.target.value, 10) || 0)}
                  data-testid="cadence-max-nudges" />
              </Field>
            </div>
            <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
              <Bell size={12} className="mt-0.5 flex-shrink-0" />
              Only one automated touch per lead per day is ever sent, even if multiple rules qualify.
            </p>
          </CardContent>
        </Card>

        {/* Activity-aware pause */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
              <Hand size={14} /> Pause when a human is involved
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Pause after agent contact (days)" hint="Don't compete with the agent">
                <Input type="number" min={0} max={60} value={cfg.pause_after_human_contact_days}
                  onChange={(e) => set('pause_after_human_contact_days', parseInt(e.target.value, 10) || 0)}
                  data-testid="cadence-pause-agent-days" />
              </Field>
              <Field label="Pause after lead replies (days)" hint="Let the conversation breathe">
                <Input type="number" min={0} max={60} value={cfg.reply_pause_days}
                  onChange={(e) => set('reply_pause_days', parseInt(e.target.value, 10) || 0)}
                  data-testid="cadence-reply-pause-days" />
              </Field>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <div className="text-sm font-medium">Pause when an appointment exists</div>
                <div className="text-xs text-slate-500">Appointment reminders still send — only sales nurture stops.</div>
              </div>
              <Switch checked={!!cfg.pause_when_appointment} onCheckedChange={(v) => set('pause_when_appointment', v)} data-testid="cadence-pause-appt-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Channel priority */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
              <MessageSquare size={14} /> Channel priority
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <Field label="Automated nurture channel" hint="Use SMS only where it adds value">
                <Select value={cfg.channel_preference} onValueChange={(v) => set('channel_preference', v)}>
                  <SelectTrigger data-testid="cadence-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms_hot_only">SMS for HOT, CRM reminder for WARM (recommended)</SelectItem>
                    <SelectItem value="sms">SMS for all nurture touches</SelectItem>
                    <SelectItem value="notify_only">CRM reminder only (no automated SMS)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <div className="text-sm font-medium">Only nurture HOT / WARM leads</div>
                  <div className="text-xs text-slate-500">Keeps volume and cost down.</div>
                </div>
                <Switch checked={cfg.require_hot_warm !== false} onCheckedChange={(v) => set('require_hot_warm', v)} data-testid="cadence-hotwarm-switch" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status policy */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
              <ListChecks size={14} /> Allowed generic nurture by lead status
            </div>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {STATUS_ROWS.map((r) => (
                <div key={r.key} className="flex items-center justify-between px-4 py-2.5" data-testid={`status-policy-row-${r.key}`}>
                  <div>
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.hint}</div>
                  </div>
                  <Switch
                    checked={!!(sp[r.key] && sp[r.key].allow)}
                    onCheckedChange={(v) => setStatus(r.key, v)}
                    data-testid={`status-policy-switch-${r.key}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Timing + hours */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
              <Zap size={14} /> Timing &amp; business hours
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="First HOT touch (hrs)" hint="After last activity">
                <Input type="number" min={1} max={168} value={cfg.hot_initial_hours}
                  onChange={(e) => set('hot_initial_hours', parseInt(e.target.value, 10) || 0)} data-testid="cadence-hot-hours" />
              </Field>
              <Field label="First WARM touch (hrs)" hint="Longer than HOT">
                <Input type="number" min={1} max={336} value={cfg.warm_initial_hours}
                  onChange={(e) => set('warm_initial_hours', parseInt(e.target.value, 10) || 0)} data-testid="cadence-warm-hours" />
              </Field>
              <Field label="Send from (hour)" hint="Agent local time">
                <Input type="number" min={0} max={23} value={cfg.quiet_hours_start}
                  onChange={(e) => set('quiet_hours_start', parseInt(e.target.value, 10) || 0)} data-testid="cadence-quiet-start" />
              </Field>
              <Field label="Send until (hour)" hint="No sends after">
                <Input type="number" min={1} max={24} value={cfg.quiet_hours_end}
                  onChange={(e) => set('quiet_hours_end', parseInt(e.target.value, 10) || 0)} data-testid="cadence-quiet-end" />
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Live preview — who is getting messaged and why */}
        <Card data-testid="nurture-preview-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
                <Eye size={14} /> Who is getting messaged — and why
              </div>
              <Button size="sm" variant="outline" onClick={loadPreview} disabled={previewLoading} data-testid="nurture-preview-refresh">
                {previewLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Refresh
              </Button>
            </div>

            {preview && (
              <div className="flex flex-wrap gap-2" data-testid="nurture-preview-counts">
                {Object.entries(preview.counts || {}).sort((a, b) => b[1] - a[1]).map(([state, n]) => (
                  <Badge key={state} className={`text-xs border ${STATE_META[state]?.color || 'bg-slate-100 text-slate-600'}`}>
                    {STATE_META[state]?.label || state}: {n}
                  </Badge>
                ))}
                {Object.keys(preview.counts || {}).length === 0 && (
                  <span className="text-xs text-slate-500">No HOT/WARM leads in scope yet.</span>
                )}
              </div>
            )}

            {preview && (preview.rows || []).length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Nurture state</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Channel</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((r) => (
                      <TableRow key={r.lead_id} data-testid={`nurture-preview-row-${r.lead_id}`}>
                        <TableCell className="font-medium text-sm">{r.name}</TableCell>
                        <TableCell className="text-xs text-slate-600">{r.status}</TableCell>
                        <TableCell className="text-xs uppercase text-slate-600">{r.score || '—'}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${STATE_META[r.state]?.color || 'bg-slate-100 text-slate-600'}`}>
                            {STATE_META[r.state]?.label || r.state}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-xs">{r.reason}</TableCell>
                        <TableCell className="text-xs text-slate-600">{r.channel === 'notify' ? 'CRM reminder' : 'SMS'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[10.5px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
